import React, { useMemo } from "react";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import { Icon } from "@foris/avocado-icons";
import { Link, Tag, Text } from "@foris/avocado-suite";
import { getRestrictionValue } from "@utils/restrictions";
import useGroupRestrictionsValues from "@dimensions/group/hooks/useGroupRestrictionsValues";
import styles from "./restrictions.module.scss";

interface RestrictionsProps {
  className?: string;
  onShowRestrictionsDrawer?: () => void;
}

const Restrictions = ({ className, onShowRestrictionsDrawer }: RestrictionsProps) => {
  const { t } = useTranslation();

  const { selectedRestrictionsByPropertyKey } = useGroupRestrictionsValues();

  const selectedRestrictionsWithValues = useMemo(() => {
    return Object.values(selectedRestrictionsByPropertyKey).filter(
      ({ values }: any) => values?.length > 0,
    );
  }, [selectedRestrictionsByPropertyKey]);

  return (
    <section className={cx(styles.restrictions, "avocado-dark-theme", className)}>
      <div className={styles.restrictions_actions}>
        <Text weight="medium" className={styles.actionsTitle}>
          {t("group-restrictions.dimension-header.title")}
        </Text>

        <Link
          size="sm"
          iconLeft="edit"
          className={styles.actionsLink}
          onClick={onShowRestrictionsDrawer}
        >
          {t("group-restrictions.dimension-header.edit")}
        </Link>
      </div>

      <div className={styles.restrictions_content}>
        {selectedRestrictionsWithValues.map(({ restriction, values }: any) => (
          <div className={styles.restriction} key={restriction?.value}>
            <div className={styles.restriction_props}>
              <Text className={styles.restrictionProp} weight="medium">
                {restriction?.label}
              </Text>
              <Text className={styles.restrictionCondition} type="sm">
                {restriction?.operatorLabel}
              </Text>
            </div>

            <div className={styles.restriction_values}>
              {values?.map(value => {
                const valueData = getRestrictionValue(value);

                return (
                  <Tag
                    key={valueData?.code}
                    className={cx(
                      styles.restrictionValue,
                      value?.isInherited && styles.restrictionValue__inherited,
                    )}
                  >
                    {value?.isInherited && (
                      <Icon className={styles.restrictionValue_icon} name="lock" color="gray-50" />
                    )}
                    {valueData?.code}
                    {valueData?.code && valueData?.name && " | "}
                    {valueData?.name}
                  </Tag>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Restrictions;
